import { useEffect, useState } from 'react';
import { getExternalEmrCustodyFilterPatientsExternalEmrCustodyFilterGet } from '../services/api/sdk.gen';
import { getHttpStatus } from '../utils/httpErrorUtils';

/** Checkbox label for the Roger-only task filter. */
export const ROGER_TASKS_FILTER_LABEL = 'Show only Roger EMR tasks';
/**
 * Tooltip for Hotbox custody=`roger`.
 *
 * Billing custody (EXTERNAL periods / unmoved charts) plus the shared
 * per-patient charts-from exclusion so Hotbox
 * “excludes pre-takeover items” when this box is checked.
 */
export const ROGER_TASKS_FILTER_TOOLTIP =
  'Hides External EMR–billed work, charts that have not moved to Roger, and tasks dated before the patient’s take-over.';

export type RogerTasksFilterState = 'loading' | 'offered' | 'not_offered';

/**
 * Whether task lists offer "Show only Roger EMR tasks", read once per mount.
 *
 * Offered while the External EMR holds current work in the agency, or while
 * its import is on (`GET /patients/external-emr/custody-filter`). A failed
 * read counts as not offered: the list then shows every task, so nobody is
 * left behind a filter they cannot see. A 403 is the expected answer for an
 * agency without Roger EMR, so it is not logged.
 */
export function useRogerTasksFilter(): RogerTasksFilterState {
  const [state, setState] = useState<RogerTasksFilterState>('loading');
  useEffect(() => {
    let alive = true;
    getExternalEmrCustodyFilterPatientsExternalEmrCustodyFilterGet()
      .then(response => {
        if (!alive) return;
        if (response.error || !response.data) {
          if (getHttpStatus(response) !== 403) {
            console.error('Failed to load the Roger tasks filter', response);
          }
          setState('not_offered');
          return;
        }
        setState(response.data.offered ? 'offered' : 'not_offered');
      })
      .catch((error: unknown) => {
        if (!alive) return;
        console.error('Failed to load the Roger tasks filter', error);
        setState('not_offered');
      });
    return () => {
      alive = false;
    };
  }, []);
  return state;
}
